import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, ArrowLeft, User, Edit, Trash2, Tag as TagIcon, Share2, Twitter, Facebook, Linkedin, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { getAdminToken, removeAdminToken } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogAdminControls = ({ slug, handleDelete }) => (
  <div className="absolute top-12 md:top-20 right-6 flex items-center gap-2">
      <Link 
          to={`/admin/edit/${slug}`}
          className="flex items-center gap-1 text-sm font-medium bg-secondary text-secondary-foreground px-3 py-1.5 rounded-md hover:bg-muted border border-border"
      >
          <Edit size={14} /> Edit
      </Link>
      <button 
          onClick={handleDelete}
          className="flex items-center gap-1 text-sm font-medium bg-destructive/10 text-destructive px-3 py-1.5 rounded-md hover:bg-destructive/20 border border-destructive/20"
      >
          <Trash2 size={14} /> Delete
      </button>
  </div>
);

const BlogHeader = ({ blog }) => {
  const publishedDate = new Date(blog.published_at);
  const updatedDate = blog.updated_at ? new Date(blog.updated_at) : null;
  return (
    <div className="mb-8 md:mb-10">
      <div className="flex flex-wrap gap-2 mb-4 md:mb-6">
        {blog.category && (
          <span className="bg-accent/10 text-accent px-2.5 md:px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
            {blog.category}
          </span>
        )}
        {blog.tags && blog.tags.map(tag => (
          <span key={tag} className="bg-secondary px-2.5 md:px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider text-secondary-foreground">
            {tag}
          </span>
        ))}
      </div>
      
      <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-extrabold tracking-tight mb-4 md:mb-6 leading-tight pr-20 md:pr-24">
        {blog.title}
      </h1>

      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 md:gap-6 text-xs md:text-sm font-medium text-muted-foreground border-b border-border pb-6 md:pb-8">
        <div className="flex items-center gap-2">
          <User size={16} className="text-foreground" />
          <span className="text-foreground">{blog.author}</span>
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Calendar size={16} />
            <span className="hidden sm:inline">Published: {publishedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
            <span className="sm:hidden">{publishedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
          </div>
          {updatedDate && updatedDate.getTime() > publishedDate.getTime() + 86400000 && (
            <div className="flex items-center gap-2 text-xs">
              <span className="opacity-70">Updated: {updatedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Clock size={16} />
          {blog.read_time}
        </div>
      </div>
    </div>
  );
};

const SocialShare = ({ url, title }) => {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  
  return (
    <div className="flex items-center gap-3 py-6 border-y border-border my-8">
      <span className="text-sm font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
        <Share2 size={16} /> Share
      </span>
      <a href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`} target="_blank" rel="noreferrer" className="p-2 bg-[#1DA1F2]/10 text-[#1DA1F2] hover:bg-[#1DA1F2]/20 rounded-full transition-colors">
        <Twitter size={18} />
      </a>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`} target="_blank" rel="noreferrer" className="p-2 bg-[#1877F2]/10 text-[#1877F2] hover:bg-[#1877F2]/20 rounded-full transition-colors">
        <Facebook size={18} />
      </a>
      <a href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}`} target="_blank" rel="noreferrer" className="p-2 bg-[#0A66C2]/10 text-[#0A66C2] hover:bg-[#0A66C2]/20 rounded-full transition-colors">
        <Linkedin size={18} />
      </a>
    </div>
  );
};

const TableOfContents = ({ content }) => {
  // Simple regex to extract H2s
  const headings = content.match(/^## (.*$)/gim);
  if (!headings || headings.length < 2) return null;

  return (
    <div className="bg-muted/50 border border-border p-6 rounded-xl mb-10">
      <h3 className="text-lg font-bold font-heading mb-4">Table of Contents</h3>
      <ul className="space-y-2 text-sm">
        {headings.map((h, i) => {
          const text = h.replace(/^## /, '');
          const slug = text.toLowerCase().replace(/[^\w]+/g, '-');
          return (
            <li key={i}>
              <a href={`#${slug}`} className="text-muted-foreground hover:text-accent transition-colors">
                {text}
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const isAdmin = !!getAdminToken();

  useEffect(() => {
    let mounted = true;
    const fetchBlogAndRecent = async () => {
      try {
        const [blogRes, recentRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/blogs/${slug}`),
          axios.get(`${BACKEND_URL}/api/blogs`)
        ]);
        if (mounted) {
          setBlog(blogRes.data);
          // Filter out current blog and keep max 3
          setRecentBlogs(recentRes.data.filter(b => b.slug !== slug).slice(0, 3));
        }
      } catch (err) {
        if (mounted) setError(true);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchBlogAndRecent();
    return () => { mounted = false; };
  }, [slug]);

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    
    try {
      const token = getAdminToken();
      await axios.delete(`${BACKEND_URL}/api/blogs/${slug}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted successfully');
      navigate('/blog');
    } catch (err) {
      toast.error('Failed to delete post');
      if (err?.response?.status === 401) {
          removeAdminToken();
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="animate-spin w-8 h-8 border-4 border-accent border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="w-full max-w-3xl mx-auto px-6 py-20 text-center">
        <h2 className="text-3xl font-bold mb-4">Post not found</h2>
        <p className="text-muted-foreground mb-8">The article you're looking for doesn't exist.</p>
        <Link to="/blog" className="text-accent hover:underline font-medium inline-flex items-center">
          <ArrowLeft size={16} className="mr-2" /> Back to Blog
        </Link>
      </div>
    );
  }

  const currentUrl = `https://supertags.online/blog/${blog.slug}`;
  const seoTitle = blog.seo_title || `${blog.title} | SuperTags Blog`;
  const metaDesc = blog.meta_description || blog.excerpt;
  const pubDate = new Date(blog.published_at).toISOString();
  const modDate = blog.updated_at ? new Date(blog.updated_at).toISOString() : pubDate;

  // JSON-LD Schema
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": currentUrl
    },
    "headline": blog.title,
    "description": metaDesc,
    "image": blog.featured_image || "https://supertags.online/default-og.png",
    "author": {
      "@type": "Organization",
      "name": blog.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "SuperTags",
      "logo": {
        "@type": "ImageObject",
        "url": "https://supertags.online/logo.png"
      }
    },
    "datePublished": pubDate,
    "dateModified": modDate
  };

  // Custom components for Markdown to add IDs to H2 and add spacing
  const MarkdownComponents = {
    h2: ({node, children}) => {
      const text = String(children).replace(/<[^>]+>/g, '');
      const id = text.toLowerCase().replace(/[^\w]+/g, '-');
      return <h2 id={id} className="scroll-mt-24 mt-12 mb-6">{children}</h2>;
    },
    h3: ({node, children}) => {
      return <h3 className="mt-8 mb-4">{children}</h3>;
    }
  };

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={currentUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:type" content="article" />
        {blog.featured_image && <meta property="og:image" content={blog.featured_image} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={metaDesc} />
        {blog.featured_image && <meta name="twitter:image" content={blog.featured_image} />}
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      <article className="w-full max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12 lg:py-20 animate-in fade-in duration-500 relative">
        <Link to="/blog" className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground mb-6 md:mb-8 transition-colors min-h-[44px]">
          <ArrowLeft size={16} className="mr-2" /> Back to Blog
        </Link>

        {isAdmin && <BlogAdminControls slug={slug} handleDelete={handleDelete} />}

        {blog.featured_image && (
          <div className="w-full aspect-video md:rounded-2xl rounded-xl overflow-hidden mb-6 md:mb-10 shadow-lg">
            <img 
              src={blog.featured_image} 
              alt={blog.title} 
              className="w-full h-full object-cover" 
              loading="lazy"
              width="1200"
              height="675"
            />
          </div>
        )}

        <BlogHeader blog={blog} />
        <TableOfContents content={blog.content} />

        <div className="prose prose-base md:prose-lg dark:prose-invert prose-headings:font-heading prose-headings:font-bold prose-a:text-accent prose-img:rounded-xl max-w-none prose-headings:scroll-mt-20">
          <ReactMarkdown components={MarkdownComponents}>{blog.content}</ReactMarkdown>
        </div>

        <SocialShare url={currentUrl} title={blog.title} />

        {/* CTA */}
        <div className="bg-gradient-to-br from-accent/10 to-blue-600/10 border border-accent/20 rounded-xl md:rounded-2xl p-6 md:p-8 text-center my-8 md:my-12">
          <h3 className="text-xl md:text-2xl font-bold font-heading mb-3 md:mb-4">Rank Higher on YouTube Today</h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Stop guessing what tags to use. Let SuperTags generate high-ranking, SEO-optimized keywords for your videos in seconds.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 bg-accent text-accent-foreground font-bold px-8 py-4 rounded-xl hover:opacity-90 transition-opacity shadow-md hover:shadow-lg">
            Generate Tags for Free <ArrowRight size={18} />
          </Link>
        </div>

        {/* Related Posts */}
        {recentBlogs.length > 0 && (
          <div className="mt-16 pt-8 border-t border-border">
            <h3 className="text-2xl font-bold font-heading mb-8">Related Articles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {recentBlogs.slice(0,2).map(recent => (
                <Link key={recent.id} to={`/blog/${recent.slug}`} className="group border border-border bg-card p-6 rounded-xl hover:border-accent/50 transition-colors flex flex-col h-full">
                  {recent.featured_image && (
                    <div className="w-full aspect-video rounded-lg overflow-hidden mb-4 shrink-0">
                      <img 
                        src={recent.featured_image} 
                        alt={recent.title} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                        loading="lazy"
                        width="600"
                        height="338"
                      />
                    </div>
                  )}
                  <div className="text-xs font-bold uppercase tracking-wider text-accent mb-2">{recent.category || 'General'}</div>
                  <h4 className="font-bold text-lg mb-2 group-hover:text-accent transition-colors leading-tight">{recent.title}</h4>
                  <div className="mt-auto flex items-center gap-4 text-xs text-muted-foreground pt-4">
                    <span className="flex items-center gap-1"><Calendar size={12}/> {new Date(recent.published_at).toLocaleDateString()}</span>
                    <span className="flex items-center gap-1"><Clock size={12}/> {recent.read_time}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </>
  );
};

export default BlogPost;
